import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Upload, FileSpreadsheet, Download, CheckCircle, AlertCircle,
  X, Image, Layers, Loader2, FileUp, Trash2, Plus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkUpload = ({ onComplete }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview/Results, 3: Media Upload
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [mediaUploading, setMediaUploading] = useState({});
  const fileInputRef = useRef(null);
  const mediaInputRef = useRef(null);
  const floorPlanInputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  };

  const validateAndSetFile = (f) => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload a CSV or Excel (.xlsx) file');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB');
      return;
    }
    setFile(f);
  };

  const downloadTemplate = async (format) => {
    try {
      
      const response = await axios.get(`${API}/partner/developer/bulk-template?format=${format}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `dreamlife_bulk_upload_template.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Template downloaded (${format.toUpperCase()})`);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/partner/developer/bulk-upload`, formData, {
        headers: {
          Authorization: '',
          'Content-Type': 'multipart/form-data'
        }
      });
      setResults(response.data);
      setStep(2);
      if (response.data.total_projects > 0) {
        toast.success(`${response.data.total_projects} projects uploaded (${response.data.total_units} units)`);
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleMediaUpload = async (propertyId, mediaType, files) => {
    const key = `${propertyId}-${mediaType}`;
    setMediaUploading(prev => ({ ...prev, [key]: true }));
    
    let successCount = 0;
    for (const f of files) {
      try {
        
        const formData = new FormData();
        formData.append('file', f);
        formData.append('media_type', mediaType);
        await axios.post(`${API}/partner/developer/properties/${propertyId}/upload-media`, formData, {
          headers: {
            Authorization: '',
            'Content-Type': 'multipart/form-data'
          }
        });
        successCount++;
      } catch {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} ${mediaType === 'floor_plan' ? 'floor plan(s)' : 'image(s)'} uploaded`);
    }
    setMediaUploading(prev => ({ ...prev, [key]: false }));
  };

  const reset = () => {
    setStep(1);
    setFile(null);
    setResults(null);
    if (onComplete) onComplete();
  };

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-4 mb-8">
        {[
          { num: 1, label: 'Upload File' },
          { num: 2, label: 'Review Results' },
          { num: 3, label: 'Add Media' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              step >= s.num ? 'bg-gold text-black' : 'bg-white/10 text-text-secondary'
            }`}>
              {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
            </div>
            <span className={`text-sm hidden sm:inline ${step >= s.num ? 'text-gold' : 'text-text-secondary'}`}>
              {s.label}
            </span>
            {i < 2 && <div className={`w-12 h-0.5 ${step > s.num ? 'bg-gold' : 'bg-white/10'}`} />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* STEP 1: File Upload */}
        {step === 1 && (
          <motion.div key="step1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            {/* Download Template */}
            <div className="glass-card p-6 mb-6">
              <h3 className="font-semibold text-lg mb-3">Step 1: Download Template</h3>
              <p className="text-text-secondary text-sm mb-4">
                Download our template, fill in your project details, then upload below. Each row represents one project.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => downloadTemplate('xlsx')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="download-xlsx-template"
                >
                  <Download className="w-4 h-4 mr-2" /> Excel Template (.xlsx)
                </Button>
                <Button
                  onClick={() => downloadTemplate('csv')}
                  variant="outline"
                  className="border-white/20 text-text-secondary hover:text-white"
                  data-testid="download-csv-template"
                >
                  <Download className="w-4 h-4 mr-2" /> CSV Template
                </Button>
              </div>
            </div>

            {/* Drop Zone */}
            <div
              className={`glass-card p-8 border-2 border-dashed transition-colors cursor-pointer ${
                file ? 'border-gold bg-gold/5' : 'border-white/20 hover:border-gold/50'
              }`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="bulk-upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => e.target.files[0] && validateAndSetFile(e.target.files[0])}
              />
              
              <div className="text-center">
                {file ? (
                  <div className="flex items-center justify-center gap-4">
                    <FileSpreadsheet className="w-12 h-12 text-gold" />
                    <div className="text-left">
                      <p className="font-semibold text-lg">{file.name}</p>
                      <p className="text-text-secondary text-sm">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                    <p className="font-semibold text-lg mb-1">Drop your file here or click to browse</p>
                    <p className="text-text-secondary text-sm">Supports CSV and Excel (.xlsx) files up to 10MB</p>
                  </>
                )}
              </div>
            </div>

            {file && (
              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="bg-gold text-black hover:bg-gold-light px-8"
                  data-testid="upload-file-btn"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <><FileUp className="w-4 h-4 mr-2" /> Upload & Import</>
                  )}
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* STEP 2: Results */}
        {step === 2 && results && (
          <motion.div key="step2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="glass-card p-5 text-center">
                <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-emerald-400">{results.total_projects}</p>
                <p className="text-text-secondary text-sm">Projects Imported</p>
              </div>
              <div className="glass-card p-5 text-center">
                <Layers className="w-8 h-8 text-gold mx-auto mb-2" />
                <p className="text-2xl font-bold text-gold">{results.total_units}</p>
                <p className="text-text-secondary text-sm">Total Units</p>
              </div>
              {results.failed > 0 && (
                <div className="glass-card p-5 text-center">
                  <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-400">{results.failed}</p>
                  <p className="text-text-secondary text-sm">Failed Rows</p>
                </div>
              )}
            </div>

            {/* Success List */}
            {results.success?.length > 0 && (
              <div className="glass-card p-5 mb-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" /> Successfully Imported
                </h3>
                <div className="space-y-2 max-h-60 overflow-auto">
                  {results.success.map((item, idx) => (
                    <div key={`success-${idx}`} className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 text-sm">
                      <span>Row {item.row}: <span className="font-semibold">{item.project_name}</span></span>
                      <span className="text-text-secondary">{item.total_units} units</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error List */}
            {results.errors?.length > 0 && (
              <div className="glass-card p-5 mb-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400" /> Errors
                </h3>
                <div className="space-y-2 max-h-60 overflow-auto">
                  {results.errors.map((item, idx) => (
                    <div key={`error-${idx}`} className="p-3 rounded-lg bg-red-500/10 text-sm">
                      <span className="font-semibold">Row {item.row} ({item.project_name}):</span>
                      <ul className="mt-1 ml-4 list-disc text-red-300">
                        {item.errors.map((err, i) => <li key={`err-${i}`}>{err}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between mt-4">
              <Button variant="outline" onClick={reset} className="border-white/20" data-testid="upload-another-btn">
                Upload Another File
              </Button>
              {results.success?.length > 0 && (
                <Button onClick={() => setStep(3)} className="bg-gold text-black hover:bg-gold-light" data-testid="add-media-btn">
                  <Image className="w-4 h-4 mr-2" /> Add Images & Floor Plans
                </Button>
              )}
            </div>
          </motion.div>
        )}

        {/* STEP 3: Media Upload */}
        {step === 3 && results?.success?.length > 0 && (
          <motion.div key="step3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <div className="glass-card p-5 mb-6">
              <h3 className="font-semibold text-lg mb-2">Add Images & 3D Floor Plans</h3>
              <p className="text-text-secondary text-sm">Upload property images and 3D floor plans for each project. You can add multiple files per project.</p>
            </div>

            <div className="space-y-4">
              {results.success.map((project) => (
                <div key={project.id} className="glass-card p-5" data-testid={`media-project-${project.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-semibold text-lg">{project.project_name}</h4>
                      <p className="text-text-secondary text-sm">{project.total_units} units</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Image Upload */}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        ref={el => { if (el) el.dataset.projectId = project.id; }}
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            handleMediaUpload(project.id, 'image', Array.from(e.target.files));
                          }
                        }}
                        id={`img-${project.id}`}
                      />
                      <label
                        htmlFor={`img-${project.id}`}
                        className="flex items-center gap-3 p-4 rounded-xl border border-dashed border-white/20 hover:border-gold/50 cursor-pointer transition-colors"
                      >
                        {mediaUploading[`${project.id}-image`] ? (
                          <Loader2 className="w-8 h-8 text-gold animate-spin" />
                        ) : (
                          <Image className="w-8 h-8 text-blue-400" />
                        )}
                        <div>
                          <p className="font-medium text-sm">Upload Images</p>
                          <p className="text-text-secondary text-xs">JPG, PNG, WebP (multiple)</p>
                        </div>
                      </label>
                    </div>

                    {/* Floor Plan Upload */}
                    <div>
                      <input
                        type="file"
                        accept="image/*,.pdf,.svg"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            handleMediaUpload(project.id, 'floor_plan', Array.from(e.target.files));
                          }
                        }}
                        id={`fp-${project.id}`}
                      />
                      <label
                        htmlFor={`fp-${project.id}`}
                        className="flex items-center gap-3 p-4 rounded-xl border border-dashed border-white/20 hover:border-gold/50 cursor-pointer transition-colors"
                      >
                        {mediaUploading[`${project.id}-floor_plan`] ? (
                          <Loader2 className="w-8 h-8 text-gold animate-spin" />
                        ) : (
                          <Layers className="w-8 h-8 text-purple-400" />
                        )}
                        <div>
                          <p className="font-medium text-sm">Upload 3D Floor Plans</p>
                          <p className="text-text-secondary text-xs">JPG, PNG, PDF, SVG (multiple)</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setStep(2)} className="border-white/20">
                Back to Results
              </Button>
              <Button onClick={reset} className="bg-gold text-black hover:bg-gold-light" data-testid="finish-bulk-upload-btn">
                <CheckCircle className="w-4 h-4 mr-2" /> Done
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BulkUpload;
